import { useState } from "react";
import { Share2, Copy, Check, MessageCircle, Send, Facebook, Twitter, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Share button + modal with native share + copy + WhatsApp/Twitter/FB/Email links.
 *  kind: "player" | "tournament"
 *  slug: the entity slug
 *  title: the displayed title in the share text
 *  accent: accent color
 */
export default function ShareButton({ kind, slug, title, accent = "#D4AF37", compact = false }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // OG-friendly URL (bots get meta tags, humans get an instant redirect)
  const shareUrl = `${BACKEND}/api/og/${kind}/${slug}`;
  const shareText =
    kind === "player"
      ? `Découvre la fiche de ${title} sur Laser Tech Création`
      : kind === "championship"
        ? `Découvre le championnat ${title} sur Laser Tech Création`
        : kind === "team"
          ? `Découvre l'équipe ${title} sur Laser Tech Création`
          : kind === "association"
            ? `Découvre l'association ${title} sur Laser Tech Création`
            : kind === "company"
              ? `Découvre ${title} sur Laser Tech Création`
              : `Découvre le tournoi ${title} sur Laser Tech Création`;

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: shareText, url: shareUrl });
        return true;
      } catch (e) {
        if (e?.name === "AbortError") return true;
      }
    }
    return false;
  };

  const onClick = async (e) => {
    e?.stopPropagation();
    const ok = await tryNativeShare();
    if (!ok) setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié dans le presse-papier");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const enc = encodeURIComponent;
  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      color: "#25D366",
      href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: Send,
      color: "#26A5E4",
      href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`,
    },
    {
      key: "twitter",
      label: "X / Twitter",
      icon: Twitter,
      color: "#FFFFFF",
      href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    },
    {
      key: "email",
      label: "Email",
      icon: Mail,
      color: "#D4AF37",
      href: `mailto:?subject=${enc(title)}&body=${enc(`${shareText}\n\n${shareUrl}`)}`,
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid={`share-btn-${kind}`}
        aria-label="Partager"
        className={
          compact
            ? "w-9 h-9 rounded-full inline-flex items-center justify-center text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
            : "inline-flex items-center gap-2 h-10 px-4 rounded-full text-xs font-bold uppercase tracking-[0.18em] border bg-white/[0.04] hover:bg-white/[0.08] transition-colors"
        }
        style={
          compact
            ? {}
            : { borderColor: `${accent}55`, color: accent }
        }
      >
        <Share2 className={compact ? "w-3.5 h-3.5" : "w-3.5 h-3.5"} />
        {!compact && <span>Partager</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="share-dialog"
          className="bg-[#0A0B0E] border border-white/10 text-white max-w-md p-0 overflow-hidden"
        >
          <DialogHeader className="px-6 pt-6 pb-2">
            <div
              className="text-[10px] font-bold uppercase tracking-[0.3em] mb-2"
              style={{ color: accent }}
            >
              Laser Tech Création
            </div>
            <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight leading-tight">
              Partager {kind === "player" ? "ce joueur" : kind === "championship" ? "ce championnat" : kind === "team" ? "cette équipe" : kind === "association" ? "cette association" : kind === "company" ? "cette entreprise" : "ce tournoi"}
            </DialogTitle>
            <p className="text-zinc-400 font-body text-sm mt-1.5">
              Aperçu auto sur WhatsApp, Insta, Twitter… avec photo, club et stats.
            </p>
          </DialogHeader>

          <div className="px-6 pb-2 mt-2">
            <div
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5"
              data-testid="share-link-row"
            >
              <div className="flex-1 min-w-0 text-xs text-zinc-300 font-body truncate select-all">
                {shareUrl}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={copy}
                data-testid="share-copy-btn"
                className="h-8 px-3 text-[10px] uppercase tracking-[0.2em] font-bold rounded-md text-black"
                style={{
                  background:
                    "linear-gradient(180deg, #FFE9A8 0%, #D4AF37 60%, #B8941F 100%)",
                }}
              >
                <AnimatePresence mode="wait">
                  {copied ? (
                    <motion.span
                      key="ok"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Copié
                    </motion.span>
                  ) : (
                    <motion.span
                      key="copy"
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="inline-flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Copier
                    </motion.span>
                  )}
                </AnimatePresence>
              </Button>
            </div>
          </div>

          <div className="px-6 py-5 grid grid-cols-5 gap-2">
            {channels.map((c) => (
              <a
                key={c.key}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-channel-${c.key}`}
                onClick={() => setOpen(false)}
                className="flex flex-col items-center gap-2 p-2 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/25 transition-all"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{
                    background: `${c.color}1F`,
                    border: `1px solid ${c.color}55`,
                  }}
                >
                  <c.icon className="w-4 h-4" style={{ color: c.color }} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 font-body">
                  {c.label}
                </span>
              </a>
            ))}
          </div>

          <div className="px-6 py-3 border-t border-white/5 text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-body text-center">
            Le lien partagé affiche photo, club et stats automatiquement
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
